import { Input } from '../common/Input'
import type { ConditionType, ConditionConfig, Thesis } from '../../types/thesis'
import { CONDITION_TYPE_LABELS } from '../../types/thesis'

interface RuleConditionBuilderProps {
  conditionType: ConditionType
  conditionConfig: ConditionConfig
  onTypeChange: (type: ConditionType) => void
  onConfigChange: (config: ConditionConfig) => void
  theses?: Thesis[]  // Optional for thesis drift conditions
}

const CONDITION_TYPES: ConditionType[] = [
  'price_below',
  'price_above',
  'price_range',
  'drawdown',
  'ratio_above',
  'ratio_below',
  'crossover_above',
  'crossover_below',
  'concentration',
  'thesis_drift_above',
  'thesis_drift_below',
  'earnings_upcoming',
  'earnings_surprise',
  'earnings_streak',
]

const INDICATORS = [
  { value: 'NAPM', label: 'ISM Manufacturing PMI (NAPM)' },
]

export function RuleConditionBuilder({
  conditionType,
  conditionConfig,
  onTypeChange,
  onConfigChange,
  theses = [],
}: RuleConditionBuilderProps) {
  const updateConfig = (key: string, value: string | number) => {
    onConfigChange({ ...conditionConfig, [key]: value })
  }

  const renderConfigFields = () => {
    switch (conditionType) {
      case 'price_below':
      case 'price_above':
        return (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Symbol"
              value={(conditionConfig as { symbol?: string }).symbol ?? ''}
              onChange={(e) => updateConfig('symbol', e.target.value.toUpperCase())}
              placeholder="e.g., AAPL"
              required
            />
            <Input
              label="Price"
              type="number"
              step="0.01"
              min={0}
              value={(conditionConfig as { value?: number }).value ?? ''}
              onChange={(e) => updateConfig('value', parseFloat(e.target.value) || 0)}
              placeholder="e.g., 150.00"
              required
            />
          </div>
        )

      case 'price_range':
        return (
          <div className="space-y-4">
            <Input
              label="Symbol"
              value={(conditionConfig as { symbol?: string }).symbol ?? ''}
              onChange={(e) => updateConfig('symbol', e.target.value.toUpperCase())}
              placeholder="e.g., IONQ"
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Low Price (Buy Zone Start)"
                type="number"
                step="0.01"
                min={0}
                value={(conditionConfig as { value_low?: number }).value_low ?? ''}
                onChange={(e) => updateConfig('value_low', parseFloat(e.target.value) || 0)}
                placeholder="e.g., 30.00"
                required
              />
              <Input
                label="High Price (Buy Zone End)"
                type="number"
                step="0.01"
                min={0}
                value={(conditionConfig as { value_high?: number }).value_high ?? ''}
                onChange={(e) => updateConfig('value_high', parseFloat(e.target.value) || 0)}
                placeholder="e.g., 35.00"
                required
              />
            </div>
          </div>
        )

      case 'drawdown':
        return (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Symbol"
              value={(conditionConfig as { symbol?: string }).symbol ?? ''}
              onChange={(e) => updateConfig('symbol', e.target.value.toUpperCase())}
              placeholder="e.g., SPY"
              required
            />
            <Input
              label="Drawdown Threshold %"
              type="number"
              step="0.1"
              min={0}
              max={100}
              value={(conditionConfig as { threshold_pct?: number }).threshold_pct ?? ''}
              onChange={(e) => updateConfig('threshold_pct', parseFloat(e.target.value) || 0)}
              placeholder="e.g., 10"
              required
            />
          </div>
        )

      case 'ratio_above':
      case 'ratio_below':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Numerator Symbol"
                value={(conditionConfig as { numerator?: string }).numerator ?? ''}
                onChange={(e) => updateConfig('numerator', e.target.value.toUpperCase())}
                placeholder="e.g., XAU/USD or GLD"
                required
              />
              <Input
                label="Denominator Symbol"
                value={(conditionConfig as { denominator?: string }).denominator ?? ''}
                onChange={(e) => updateConfig('denominator', e.target.value.toUpperCase())}
                placeholder="e.g., XAG/USD or SLV"
                required
              />
            </div>
            <Input
              label="Ratio Threshold"
              type="number"
              step="0.1"
              min={0}
              value={(conditionConfig as { threshold?: number }).threshold ?? ''}
              onChange={(e) => updateConfig('threshold', parseFloat(e.target.value) || 0)}
              placeholder="e.g., 80"
              required
            />
          </div>
        )

      case 'crossover_above':
      case 'crossover_below':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Indicator
              </label>
              <select
                value={(conditionConfig as { indicator?: string }).indicator ?? ''}
                onChange={(e) => updateConfig('indicator', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              >
                <option value="">Select indicator...</option>
                {INDICATORS.map((ind) => (
                  <option key={ind.value} value={ind.value}>
                    {ind.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Threshold Value"
              type="number"
              step="0.1"
              value={(conditionConfig as { threshold?: number }).threshold ?? ''}
              onChange={(e) => updateConfig('threshold', parseFloat(e.target.value) || 0)}
              placeholder="e.g., 50"
              required
            />
          </div>
        )

      case 'concentration':
        return (
          <Input
            label="Concentration Threshold %"
            type="number"
            step="0.1"
            min={0}
            max={100}
            value={(conditionConfig as { threshold_pct?: number }).threshold_pct ?? ''}
            onChange={(e) => updateConfig('threshold_pct', parseFloat(e.target.value) || 0)}
            placeholder="e.g., 12"
            required
          />
        )

      case 'thesis_drift_above':
      case 'thesis_drift_below':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Thesis
              </label>
              <select
                value={(conditionConfig as { thesis_id?: string }).thesis_id ?? ''}
                onChange={(e) => updateConfig('thesis_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              >
                <option value="">Select thesis...</option>
                {theses
                  .filter((t) => t.target_allocation_pct !== null && t.target_allocation_pct !== undefined)
                  .map((thesis) => (
                    <option key={thesis.id} value={thesis.id}>
                      {thesis.name} (target: {thesis.target_allocation_pct}%)
                    </option>
                  ))}
              </select>
              {theses.length === 0 && (
                <p className="mt-1 text-xs text-yellow-600">
                  No theses available. Create theses with target allocations first.
                </p>
              )}
            </div>
            <Input
              label="Drift Threshold %"
              type="number"
              step="0.1"
              min={0}
              max={100}
              value={(conditionConfig as { threshold_pct?: number }).threshold_pct ?? ''}
              onChange={(e) => updateConfig('threshold_pct', parseFloat(e.target.value) || 0)}
              placeholder="e.g., 10"
              required
            />
            <p className="text-xs text-gray-500">
              {conditionType === 'thesis_drift_above'
                ? 'Triggers when current allocation exceeds target by more than threshold'
                : 'Triggers when current allocation is below target by more than threshold'}
            </p>
          </div>
        )

      case 'earnings_upcoming':
        return (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Symbol"
              value={(conditionConfig as { symbol?: string }).symbol ?? ''}
              onChange={(e) => updateConfig('symbol', e.target.value.toUpperCase())}
              placeholder="e.g., AAPL"
              required
            />
            <Input
              label="Days Before Earnings"
              type="number"
              step="1"
              min={1}
              max={90}
              value={(conditionConfig as { days?: number }).days ?? ''}
              onChange={(e) => updateConfig('days', parseInt(e.target.value) || 7)}
              placeholder="e.g., 7"
              required
            />
          </div>
        )

      case 'earnings_surprise':
        return (
          <div className="space-y-4">
            <Input
              label="Symbol"
              value={(conditionConfig as { symbol?: string }).symbol ?? ''}
              onChange={(e) => updateConfig('symbol', e.target.value.toUpperCase())}
              placeholder="e.g., AAPL"
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Direction
                </label>
                <select
                  value={(conditionConfig as { direction?: string }).direction ?? 'beat'}
                  onChange={(e) => updateConfig('direction', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="beat">Beat (positive surprise)</option>
                  <option value="miss">Miss (negative surprise)</option>
                </select>
              </div>
              <Input
                label="Threshold %"
                type="number"
                step="0.1"
                min={0}
                value={(conditionConfig as { threshold_pct?: number }).threshold_pct ?? ''}
                onChange={(e) => updateConfig('threshold_pct', parseFloat(e.target.value) || 0)}
                placeholder="e.g., 5.0"
                required
              />
            </div>
          </div>
        )

      case 'earnings_streak':
        return (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Symbol"
              value={(conditionConfig as { symbol?: string }).symbol ?? ''}
              onChange={(e) => updateConfig('symbol', e.target.value.toUpperCase())}
              placeholder="e.g., AAPL"
              required
            />
            <Input
              label="Streak Length"
              type="number"
              step="1"
              min={1}
              max={20}
              value={(conditionConfig as { streak?: number }).streak ?? ''}
              onChange={(e) => updateConfig('streak', parseInt(e.target.value) || 4)}
              placeholder="e.g., 4"
              required
            />
          </div>
        )

      default:
        return null
    }
  }

  const getConditionDescription = () => {
    switch (conditionType) {
      case 'price_below':
        return 'Triggers when the symbol price drops below the specified value'
      case 'price_above':
        return 'Triggers when the symbol price rises above the specified value'
      case 'price_range':
        return 'Triggers when the symbol price enters the buy zone range'
      case 'drawdown':
        return 'Triggers when the symbol drops by the threshold % from its high'
      case 'ratio_above':
        return 'Triggers when numerator/denominator ratio exceeds threshold'
      case 'ratio_below':
        return 'Triggers when numerator/denominator ratio drops below threshold'
      case 'crossover_above':
        return 'Triggers when indicator crosses above threshold (e.g., PMI > 50)'
      case 'crossover_below':
        return 'Triggers when indicator crosses below threshold (e.g., PMI < 50)'
      case 'concentration':
        return 'Triggers when any single position exceeds the threshold % of portfolio'
      case 'thesis_drift_above':
        return 'Triggers when thesis is over-allocated beyond threshold vs target'
      case 'thesis_drift_below':
        return 'Triggers when thesis is under-allocated beyond threshold vs target'
      case 'earnings_upcoming':
        return 'Triggers when earnings announcement is within N days'
      case 'earnings_surprise':
        return 'Triggers when most recent earnings beat/miss exceeds threshold'
      case 'earnings_streak':
        return 'Triggers when company has consecutive beat/miss streak of N quarters'
      default:
        return ''
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Condition Type
        </label>
        <select
          value={conditionType}
          onChange={(e) => {
            onTypeChange(e.target.value as ConditionType)
            onConfigChange({}) // Reset config when type changes
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          required
        >
          {CONDITION_TYPES.map((type) => (
            <option key={type} value={type}>
              {CONDITION_TYPE_LABELS[type]}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">{getConditionDescription()}</p>
      </div>

      <div className="p-4 bg-gray-50 rounded-lg">{renderConfigFields()}</div>
    </div>
  )
}
